import { useEffect, useRef, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getStoredRef, clearStoredRef } from "@/lib/referral";

/**
 * After a vet signs up and confirms their email, this hook
 * creates their practice and approval request on first login.
 * It reads practice details from user_metadata set during signup.
 * It also bootstraps a referral code for the new vet, and (if they
 * arrived via a referral link) records the pending referral row.
 */
export const useVetOnboarding = (user: User | null) => {
  const [loading, setLoading] = useState(false);
  const completedForUserId = useRef<string | null>(null);
  const inFlightForUserId = useRef<string | null>(null);
  const latestUser = useRef(user);
  latestUser.current = user;

  const userId = user?.id ?? null;
  const isVetSignup = user?.user_metadata?.is_vet === true;

  useEffect(() => {
    if (!user) {
      completedForUserId.current = null;
      inFlightForUserId.current = null;
      setLoading(false);
      return;
    }

    if (completedForUserId.current === user.id) {
      setLoading(false);
      return;
    }

    // Prevent the effect re-firing (Strict Mode, auth refresh, remount) from
    // launching a second provision() before the first one completes - that race
    // was creating duplicate vet_practices rows.
    if (inFlightForUserId.current === user.id) {
      return;
    }
    inFlightForUserId.current = user.id;

    setLoading(true);

    const provision = async () => {
      try {
        // Determine whether this user is a vet: either flagged in metadata at
        // signup, or already has the 'vet' role in the database.
        const { data: roleRow } = await supabase
          .from("user_roles")
          .select("id, role")
          .eq("user_id", user.id)
          .maybeSingle();

        // Google supplies vet metadata after the first session arrives. Read
        // the latest same-user snapshot even if that update arrived mid-query.
        if (latestUser.current?.id !== user.id) return;
        const meta = latestUser.current.user_metadata ?? {};
        const isVet = meta?.is_vet === true || roleRow?.role === "vet";
        if (!isVet) {
          // Do not cache this early exit: OAuth can still add signup intent.
          return;
        }

        // The vet role is granted by an admin after review; it cannot be self-assigned.

        // Ensure a practice row exists (covers both new signups and any vet
        // whose practice somehow wasn't created on signup).
        const { data: existingPractice } = await supabase
          .from("vet_practices")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!existingPractice) {
          const claimId = typeof meta.claim_practice_id === "string" ? meta.claim_practice_id : null;
          let claimed = false;
          if (claimId) {
            const { error: claimErr } = await (supabase as any).rpc("claim_vet_practice", {
              _practice_id: claimId,
            });
            if (!claimErr) claimed = true;
            else console.warn("[useVetOnboarding] claim failed:", claimErr.message);
          }
          if (!claimed) {
            // slug is auto-generated by a BEFORE INSERT trigger; cast to any
            // so TS doesn't complain about it being a required field here.
            await supabase.from("vet_practices").insert({
              user_id: user.id,
              name: meta.practice_name || "My Practice",
              address: meta.practice_address || null,
              postcode: meta.practice_postcode || null,
              phone: meta.practice_phone || null,
              email: meta.practice_email || null,
              description: meta.practice_description || null,
            } as any);
          }
        }

        const rcvsNumber = typeof meta.rcvs_number === "string" ? meta.rcvs_number.trim() : "";

        // Vet access is granted by an admin. Record a pending request so the
        // admin queue shows this vet (unique per user; duplicates are ignored).
        if (roleRow?.role !== "vet") {
          const { data: practiceRow } = await supabase
            .from("vet_practices")
            .select("id, name")
            .eq("user_id", user.id)
            .maybeSingle();
          const fullName = [meta.first_name, meta.last_name].filter(Boolean).join(" ") || meta.display_name || null;
          const { error: reqErr } = await (supabase as any).from("vet_signup_requests").insert({
            user_id: user.id,
            practice_id: practiceRow?.id ?? null,
            practice_name: practiceRow?.name ?? meta.practice_name ?? null,
            email: user.email ?? null,
            full_name: fullName,
            rcvs_number: rcvsNumber || null,
          });
          if (reqErr && reqErr.code !== "23505") console.warn("[useVetOnboarding] signup request failed:", reqErr.message);
        }

        // If the vet supplied an RCVS number at signup, try to auto-verify
        // their identity by matching against scraped RCVS listings.
        if (rcvsNumber) {
          try {
            await (supabase as any).rpc("auto_verify_vet_by_rcvs", {
              _rcvs_number: rcvsNumber,
            });
          } catch (err) {
            console.warn("[useVetOnboarding] auto_verify_vet_by_rcvs failed:", err);
          }
        }

        // Always ensure the vet has a referral code of their own.
        const { data: codeRow } = await (supabase as any)
          .from("vet_referral_codes")
          .select("code")
          .eq("user_id", user.id)
          .maybeSingle();
        if (!codeRow) {
          const { data: generated } = await (supabase as any).rpc("generate_referral_code", {
            _user_id: user.id,
          });
          if (generated) {
            await (supabase as any)
              .from("vet_referral_codes")
              .insert({ user_id: user.id, code: generated });
          }
        }

        // If they arrived via a referral link, create the pending referral row.
        const ref = getStoredRef();
        if (ref) {
          try {
            const { data: refOwnerId } = await (supabase as any).rpc(
              "lookup_referrer_by_code",
              { _code: ref },
            );
            if (refOwnerId && refOwnerId !== user.id) {
              const { data: existingRefRow } = await (supabase as any)
                .from("vet_referrals")
                .select("id")
                .eq("referee_user_id", user.id)
                .maybeSingle();
              if (!existingRefRow) {
                await (supabase as any).from("vet_referrals").insert({
                  referrer_user_id: refOwnerId,
                  referee_user_id: user.id,
                  code: ref,
                });
              }
            }
          } finally {
            clearStoredRef();
          }
        }

        completedForUserId.current = user.id;
      } finally {
        inFlightForUserId.current = null;
        setLoading(false);
      }
    };

    void provision();
  }, [userId, isVetSignup]);

  return { onboardingLoading: loading };
};
